import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Compass, Swords } from 'lucide-react';
import { ChatMessage } from '@/components/chat/ChatMessage';
import { ChatComposer } from './ChatComposer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImageModal from '@/components/common/ImageModal';
import { GameTimeDisplay } from '@/components/game/GameTimeDisplay';
import type { GameUIMessage } from '@/lib/stores/gameStore';
import { useGameStore } from '@/lib/stores/gameStore';
import { useChatScrollKickStore } from '@/lib/stores/chatScrollKickStore';
import { useMessageQueue } from '@/hooks/useMessageQueue';
import { AT_BOTTOM_THRESHOLD_PX, useStickToBottom } from '@/hooks/useStickToBottom';
import { TypingIndicator } from './TypingIndicator';
import { CharacterTypeEnum } from '@danda/shared';

interface GameChatContentProps {
  isActive: boolean;
  messages: GameUIMessage[];
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  inputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  isConnected: boolean;
  /** Filtered slash commands/options for game action menu */
  slashCommands?: import('../chat-commands').SlashCommandDefinition[];
  /** Currently selected index in slash menu */
  slashCommandIndex?: number;
  /** Callback when a slash command/option is selected */
  onSlashCommandSelect?: (cmd: import('../chat-commands').SlashCommandDefinition) => void;
  /** Callback to close the slash command menu */
  onSlashCommandClose?: () => void;
  /** Stage label for multi-step flows */
  slashStageLabel?: string | null;
  /** Whether the slash menu state machine is active (for empty state display) */
  slashMenuActive?: boolean;
  /** ID of the active (selected) menu item for aria-activedescendant */
  activeDescendantId?: string;
}

export function GameChatContent({
  isActive,
  messages,
  virtuosoRef,
  inputRef,
  inputValue,
  onInputChange,
  onKeyPress,
  onSend,
  isConnected,
  slashCommands,
  slashCommandIndex = 0,
  onSlashCommandSelect,
  onSlashCommandClose,
  slashStageLabel,
  slashMenuActive,
  activeDescendantId,
}: GameChatContentProps) {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [portraitOverlay, setPortraitOverlay] = useState<{ url: string; name: string } | null>(
    null
  );

  const { userScrolledUp, userScrolledUpRef, setScroller, resetUserScrolledUp } =
    useStickToBottom();

  // Get party and scene character info from game store
  const {
    partyMembers,
    sceneCharacters,
    currentRound,
    encounterType,
    lastRoundProcessingMs,
    isWaitingForRound,
  } = useGameStore();

  // Player-intent kick — ChatPanel bumps the counter on every local
  // submission. We can't identify local echoes by senderId alone (server
  // re-broadcasts player actions with the same character senderId), so
  // we hook the kick at submission time instead.
  const scrollKick = useChatScrollKickStore((s) => s.kick);

  // Message queue (fake-streaming progressive reveal)
  const { revealingMessageId, onRevealComplete, isQueueActive, finishReveal, skipQueue } =
    useMessageQueue();

  // Auto-scroll during progressive reveal — the currently revealing bubble
  // grows taller as text animates in, but Virtuoso's followOutput only
  // triggers on new items, not height changes. This interval keeps the
  // bottom edge visible UNLESS the user has explicitly scrolled up.
  useEffect(() => {
    if (!revealingMessageId) return;
    const interval = setInterval(() => {
      if (userScrolledUpRef.current) return;
      virtuosoRef.current?.scrollToIndex({
        index: 'LAST',
        align: 'end',
      });
    }, 100);
    return () => clearInterval(interval);
  }, [revealingMessageId, virtuosoRef, userScrolledUpRef]);

  // After reveal completes, do one final scroll to re-anchor at bottom.
  const prevRevealingRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevRevealingRef.current && !revealingMessageId) {
      const timer = setTimeout(() => {
        if (userScrolledUpRef.current) return;
        virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
      }, 50);
      return () => clearTimeout(timer);
    }
    prevRevealingRef.current = revealingMessageId;
  }, [revealingMessageId, virtuosoRef, userScrolledUpRef]);

  // Live-streaming bubble grows in height as WS chunks arrive but Virtuoso's
  // followOutput only fires on new items, not in-place text changes. Poll
  // every 100ms while ANY message is live-streaming AND auto-scroll is on.
  const hasLiveStreaming = messages.some((m) => m.isStreaming);
  useEffect(() => {
    if (!hasLiveStreaming) return;
    const interval = setInterval(() => {
      if (userScrolledUpRef.current) return;
      virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
    }, 100);
    return () => clearInterval(interval);
  }, [hasLiveStreaming, virtuosoRef, userScrolledUpRef]);

  // Re-arm auto-follow + scroll on player-intent submission.
  const firstKickSeen = useRef(scrollKick);
  useEffect(() => {
    if (firstKickSeen.current === scrollKick) return; // mount tick, ignore
    firstKickSeen.current = scrollKick;
    resetUserScrolledUp();
    virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end' });
  }, [scrollKick, resetUserScrolledUp, virtuosoRef]);

  // ArrowDown: skip current bubble. Escape: dump all remaining messages instantly.
  useEffect(() => {
    if (!isQueueActive) return;
    const handler = (e: KeyboardEvent) => {
      // Skip if already handled by slash command menu (e.g., Esc to go back a step)
      if (e.defaultPrevented) return;
      if (e.key === 'ArrowDown' && revealingMessageId) {
        e.preventDefault();
        finishReveal(revealingMessageId);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        skipQueue();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isQueueActive, revealingMessageId, finishReveal, skipQueue]);

  // Track scroll position via Virtuoso's built-in callbacks
  // These are more reliable than computing from rangeChanged during streaming layout shifts
  const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
    setIsScrolledToBottom(atBottom);
  }, []);

  const handleAtTopStateChange = useCallback((atTop: boolean) => {
    setIsScrolledToTop(atTop);
  }, []);

  // Scroll to bottom handler — also resets the "user scrolled up" flag
  // so subsequent messages resume auto-follow.
  const handleScrollToBottom = useCallback(() => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'smooth',
      });
      resetUserScrolledUp();
    }
  }, [virtuosoRef, resetUserScrolledUp]);

  // Scroll to top handler
  const handleScrollToTop = useCallback(() => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: 0,
        align: 'start',
        behavior: 'smooth',
      });
    }
  }, [virtuosoRef]);

  return (
    <div className="flex-1 flex flex-col mx-4 min-h-0 overflow-hidden relative">
      {/* Journey Header - Round and party participants */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-border flex-shrink-0">
        {/* Encounter badge + in-game time always; round # and exec time are dev-only
            debug info — import.meta.env.DEV is true in any local vite-serve session
            (dev / dev:local / dev-hybrid) and false in every built bundle (staging, prod) */}
        {currentRound > 0 && (
          <div className="flex items-center gap-2">
            {import.meta.env.DEV && (
              <>
                <Compass className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Round {currentRound}</span>
              </>
            )}
            {encounterType === 'combat' ? (
              <Badge className="bg-red-600 text-white text-xs px-1.5 py-0">
                <Swords className="h-3 w-3 mr-1" />
                Combat
              </Badge>
            ) : (
              <Badge className="bg-emerald-700 text-white text-xs px-1.5 py-0">
                <Compass className="h-3 w-3 mr-1" />
                Exploration
              </Badge>
            )}
            {import.meta.env.DEV && lastRoundProcessingMs != null && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {Math.floor(lastRoundProcessingMs / 60000)}:
                {String(Math.floor((lastRoundProcessingMs % 60000) / 1000)).padStart(2, '0')}
              </span>
            )}
            <GameTimeDisplay />
          </div>
        )}
        {/* Party member avatars */}
        {partyMembers.length > 0 && (
          <div className={`flex items-center gap-1 ${currentRound > 0 ? 'ml-auto' : ''}`}>
            {partyMembers.slice(0, 4).map((member) => (
              <Avatar
                key={member.charId}
                className={`h-6 w-6 border border-border ${member.portraitUrl ? 'cursor-pointer' : ''}`}
                title={member.name}
                onClick={
                  member.portraitUrl
                    ? () => setPortraitOverlay({ url: member.portraitUrl!, name: member.name })
                    : undefined
                }
              >
                {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={member.name} />}
                <AvatarFallback className="text-[10px] bg-secondary">
                  {member.avatarEmoji || member.name[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ))}
            {partyMembers.length > 4 && (
              <span className="text-xs text-muted-foreground">+{partyMembers.length - 4}</span>
            )}
          </div>
        )}
        {/* Scene characters (NPCs, monsters) - separated by divider */}
        {sceneCharacters.length > 0 && (
          <>
            <div className="h-4 w-px bg-border mx-1" />
            <div className="flex items-center gap-1">
              {sceneCharacters.slice(0, 4).map((char) => (
                <Avatar
                  key={char.charId}
                  className={`h-6 w-6 border border-border ${char.portraitUrl ? 'cursor-pointer' : ''}`}
                  title={`${char.name} (${char.type || 'NPC'})`}
                  onClick={
                    char.portraitUrl
                      ? () => setPortraitOverlay({ url: char.portraitUrl!, name: char.name })
                      : undefined
                  }
                >
                  {char.avatarUrl && <AvatarImage src={char.avatarUrl} alt={char.name} />}
                  <AvatarFallback className="text-[10px] bg-red-900/50 text-red-200">
                    {char.avatarEmoji ||
                      (char.type === CharacterTypeEnum.MONSTER
                        ? '👹'
                        : char.type === CharacterTypeEnum.ANIMAL
                          ? '🐾'
                          : '👤')}
                  </AvatarFallback>
                </Avatar>
              ))}
              {sceneCharacters.length > 4 && (
                <span className="text-xs text-muted-foreground">+{sceneCharacters.length - 4}</span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex-1 min-h-0 relative">
        {/* Scroll-to-top button — absolute so it never causes layout shifts */}
        {isActive && !isScrolledToTop && messages.length > 0 && (
          <div className="absolute top-2 left-0 right-0 flex justify-center z-10">
            <Button
              onClick={handleScrollToTop}
              size="icon"
              className="h-8 w-8 rounded-full shadow-lg bg-card border border-border hover:bg-accent text-card-foreground"
              aria-label="Scroll to top"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        )}
        {isActive ? (
          <Virtuoso
            ref={virtuosoRef}
            scrollerRef={(el) => {
              setScroller(el as HTMLElement | null);
            }}
            style={{ height: '100%' }}
            data={messages}
            initialTopMostItemIndex={Math.max(0, messages.length - 1)}
            computeItemKey={(_, msg) => msg.id}
            itemContent={(_, msg) => {
              const isRevealing = msg.id === revealingMessageId;
              return (
                <div className="pb-3">
                  <ChatMessage
                    id={msg.id}
                    content={msg.content}
                    senderName={msg.senderName}
                    timestamp={msg.timestamp}
                    type={msg.type}
                    container="game"
                    isStreaming={msg.isStreaming}
                    avatarUrl={msg.avatarUrl}
                    avatarEmoji={msg.avatarEmoji}
                    senderPortraitUrl={msg.senderPortraitUrl}
                    bubbleColor={msg.bubbleColor}
                    isQueuedReveal={isRevealing && msg.isQueuedReveal}
                    onRevealComplete={onRevealComplete}
                    meta={msg.meta}
                  />
                </div>
              );
            }}
            followOutput={userScrolledUp ? false : 'auto'}
            alignToBottom
            atBottomStateChange={handleAtBottomStateChange}
            atTopStateChange={handleAtTopStateChange}
            atBottomThreshold={AT_BOTTOM_THRESHOLD_PX}
            components={{
              Footer: () => <div className="h-4" />,
            }}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            Switching to game chat...
          </div>
        )}
        {/* Scroll-to-bottom button — absolute so it never causes layout shifts */}
        {isActive && !isScrolledToBottom && messages.length > 0 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10 pointer-events-none">
            <Button
              onClick={handleScrollToBottom}
              size="icon"
              className="h-8 w-8 rounded-full shadow-lg bg-card border border-border hover:bg-accent text-card-foreground pointer-events-auto"
              aria-label="Scroll to bottom"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <TypingIndicator visible={isWaitingForRound} />

      <ChatComposer
        value={inputValue}
        inputRef={inputRef}
        onChange={onInputChange}
        onKeyDown={onKeyPress}
        onSend={onSend}
        placeholder={
          isConnected
            ? 'Press / for actions, or describe what you want to do...'
            : 'Reconnecting...'
        }
        isOnline={isConnected}
        inputId="game-chat-input"
        slashCommands={slashCommands}
        slashCommandIndex={slashCommandIndex}
        onSlashCommandSelect={onSlashCommandSelect}
        onSlashCommandClose={onSlashCommandClose}
        slashStageLabel={slashStageLabel}
        slashMenuActive={slashMenuActive}
        activeDescendantId={activeDescendantId}
      />
      <ImageModal
        isOpen={!!portraitOverlay}
        onClose={() => setPortraitOverlay(null)}
        imageUrl={portraitOverlay?.url}
        altText={portraitOverlay?.name ?? ''}
      />
    </div>
  );
}
